import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, CircularProgress, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';

const fmtS = (v) => `S/ ${Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
const fmtFecha = (s) => {
  if (!s) return '';
  const str = String(s).split('T')[0].split(' ')[0];
  if (str.includes('-')) {
    const [yyyy, mm, dd] = str.split('-');
    return `${dd}/${mm}/${yyyy}`;
  }
  return str;
};

const useCountUp = (target, duration = 800) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) { setValue(0); return; }
    let start = 0; const steps = 40; const inc = target / steps; const iv = duration / steps;
    const t = setInterval(() => { start += inc; if (start >= target) { setValue(target); clearInterval(t); } else setValue(start); }, iv);
    return () => clearInterval(t);
  }, [target, duration]);
  return value;
};

// ── Helpers UI — calcados de LibroVentas.jsx ───────────────────────────────────
const Lbl = ({ children }) => (
  <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.5 }}>{children}</Typography>
);

const Sel = ({ value, onChange, children, w }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = React.useRef(null);
  const opciones = React.Children.map(children, ch => ({ value: String(ch.props.value ?? ''), label: ch.props.children })) || [];
  const selLabel = opciones.find(o => o.value === String(value || ''))?.label || '';
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);
  const handleOpen = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(v => !v); };
  return (
    <Box ref={ref} sx={{ position: 'relative', width: w || 130 }}>
      <Box onClick={handleOpen}
        sx={{ height: 34, px: 1.2, pr: 3.5, display: 'flex', alignItems: 'center',
          border: `1px solid ${open ? IOS.blue : IOS.separator}`, borderRadius: '8px',
          bgcolor: IOS.surface, cursor: 'pointer', userSelect: 'none',
          boxShadow: open ? `0 0 0 3px ${IOS.blue}18` : 'none', transition: 'all 0.15s', position: 'relative' }}>
        <Typography sx={{ fontSize: '0.8rem', color: IOS.label, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selLabel}</Typography>
        <Box sx={{ position: 'absolute', right: 10, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.2s', display: 'flex', alignItems: 'center' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round" /></svg>
        </Box>
      </Box>
      {open && rect && (
        <Box sx={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 99999,
          bgcolor: IOS.surface, border: `1px solid ${IOS.separator}`, borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)', minWidth: rect.width, maxHeight: 240,
          overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
          {opciones.map(op => (
            <Box key={op.value} onClick={() => { onChange({ target: { value: op.value } }); setOpen(false); }}
              sx={{ px: 1.5, py: 0.9, cursor: 'pointer', whiteSpace: 'nowrap',
                bgcolor: String(value || '') === op.value ? `${IOS.blue}08` : 'transparent', '&:hover': { bgcolor: IOS.bg } }}>
              <Typography sx={{ fontSize: '0.8rem', color: String(value || '') === op.value ? IOS.blue : IOS.label, fontWeight: String(value || '') === op.value ? 700 : 400 }}>{op.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const TH = ({ children, center, right, w }) => (
  <th style={{ padding: '9px 8px', width: w || 'auto', textAlign: center ? 'center' : right ? 'right' : 'left',
    fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.06em', textTransform: 'uppercase',
    backgroundColor: IOS.surface, borderBottom: `1px solid ${IOS.separator}`, whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}>
    {children}
  </th>
);

const Paginacion = ({ total, pagina, setPagina, rpp, setRpp }) => {
  const totalPags = Math.ceil(total / rpp) || 1;
  const pgBtn = (dis, onClick, lbl) => (
    <Box component="button" onClick={onClick} disabled={dis}
      sx={{ width: 28, height: 28, borderRadius: '8px', border: `1px solid ${IOS.separator}`,
        bgcolor: IOS.surface, cursor: dis ? 'default' : 'pointer', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '0.8rem', color: IOS.label, opacity: dis ? 0.3 : 1,
        transition: 'all 0.15s', '&:hover': { bgcolor: dis ? IOS.surface : IOS.bg } }}>{lbl}</Box>
  );
  return (
    <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${IOS.separator}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: '0.72rem', color: IOS.label2 }}>
          {total === 0 ? 0 : ((pagina - 1) * rpp) + 1}–{Math.min(pagina * rpp, total)} de {total.toLocaleString()}
        </Typography>
        <select value={rpp} onChange={e => { setRpp(Number(e.target.value)); setPagina(1); }}
          style={{ height: 28, padding: '0 8px', border: `1px solid ${IOS.separator}`, borderRadius: 6,
            fontSize: '0.72rem', color: IOS.label, backgroundColor: IOS.bg, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
          {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}/pág</option>)}
        </select>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {pgBtn(pagina === 1, () => setPagina(1), '«')}
        {pgBtn(pagina === 1, () => setPagina(p => Math.max(1, p - 1)), '‹')}
        {Array.from({ length: totalPags }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPags || Math.abs(p - pagina) <= 1)
          .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
          .map((p, i) => p === '…'
            ? <Typography key={`e${i}`} sx={{ px: 0.8, fontSize: '0.78rem', color: IOS.label3 }}>…</Typography>
            : <Box key={p} component="button" onClick={() => setPagina(p)}
                sx={{ width: 28, height: 28, borderRadius: '8px', border: 'none',
                  bgcolor: pagina === p ? IOS.blue : IOS.surface, color: pagina === p ? 'white' : IOS.label,
                  cursor: 'pointer', fontWeight: pagina === p ? 700 : 400, fontSize: '0.78rem',
                  boxShadow: pagina === p ? `0 2px 8px ${IOS.blue}40` : 'none', transition: 'all 0.15s' }}>{p}</Box>
          )}
        {pgBtn(pagina === totalPags, () => setPagina(p => Math.min(totalPags, p + 1)), '›')}
        {pgBtn(pagina === totalPags, () => setPagina(totalPags), '»')}
      </Box>
    </Box>
  );
};

const PillEstado = ({ val }) => {
  const ok = val === 'PAGADA';
  const parcial = val === 'PARCIAL';
  const bg = ok ? '#EDFAF2' : parcial ? '#FFF8E6' : '#FFF0EF';
  const bdr = ok ? '#B7EFD0' : parcial ? '#FFE9A0' : '#FFD4D1';
  const txt = ok ? '#1A6B3C' : parcial ? '#8B6914' : '#C0392B';
  const dot = ok ? IOS.green : parcial ? IOS.orange : IOS.red;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.8, py: 0.2, borderRadius: '20px', bgcolor: bg, border: `1px solid ${bdr}` }}>
      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: txt, whiteSpace: 'nowrap' }}>{val || 'PENDIENTE'}</Typography>
    </Box>
  );
};

const PillOrigen = ({ val }) => (
  <Box sx={{ display: 'inline-flex', px: 0.8, py: 0.2, borderRadius: '20px', bgcolor: IOS.bg, border: `1px solid ${IOS.separator}` }}>
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: IOS.label2 }}>{val}</Typography>
  </Box>
);

const KpiCard = ({ label, monto, color, icon }) => {
  const animado = useCountUp(monto, 800);
  return (
    <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.6 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 800, color, fontSize: '1.1rem', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            S/ {animado.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
        <Box sx={{ width: 38, height: 38, borderRadius: '12px', bgcolor: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
      </Box>
    </Box>
  );
};

const Modal = ({ open, onClose, children, w = 420 }) => {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;
  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, bgcolor: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <Box onClick={e => e.stopPropagation()}
        sx={{ bgcolor: IOS.surface, borderRadius: '20px', overflow: 'hidden', width: w, maxWidth: 'calc(100vw - 32px)', mx: 2,
          maxHeight: '90dvh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
        <Box sx={{ overflowY: 'auto' }}>{children}</Box>
      </Box>
    </Box>
  );
};

const ModalHeader = ({ title, sub, onClose, icon, color = IOS.blue }) => (
  <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${IOS.separator}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
      <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: IOS.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</Typography>
        {sub && <Typography sx={{ fontSize: '0.7rem', color: IOS.label2 }}>{sub}</Typography>}
      </Box>
    </Box>
    <Box component="button" onClick={onClose}
      sx={{ width: 28, height: 28, borderRadius: '50%', border: 'none', bgcolor: '#E5E5EA', cursor: 'pointer', flexShrink: 0, p: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: '#D1D1D6' } }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
    </Box>
  </Box>
);

// DatePickerIOS local — sin input nativo, portal + doble ref (mismo patrón del resto del sistema)
function DatePickerIOS({ value, onChange, placeholder = 'Fecha' }) {
  const [abierto, setAbierto] = useState(false);
  const [mesVista, setMesVista] = useState(() => {
    const d = value ? new Date(value + 'T00:00:00') : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const triggerRef = React.useRef(null);
  const popupRef = React.useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const dentroTrigger = triggerRef.current?.contains(e.target);
      const dentroPopup = popupRef.current?.contains(e.target);
      if (!dentroTrigger && !dentroPopup) setAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fmt = (iso) => {
    if (!iso) return placeholder;
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  const diasSemana = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
  const primerDiaMes = new Date(mesVista.getFullYear(), mesVista.getMonth(), 1);
  const offsetInicio = primerDiaMes.getDay();
  const diasEnMes = new Date(mesVista.getFullYear(), mesVista.getMonth() + 1, 0).getDate();
  const celdas = [];
  for (let i = 0; i < offsetInicio; i++) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d++) celdas.push(d);

  const hoy = new Date();
  const esHoy = (d) => d && mesVista.getFullYear() === hoy.getFullYear() && mesVista.getMonth() === hoy.getMonth() && d === hoy.getDate();
  const esSeleccionado = (d) => {
    if (!d || !value) return false;
    const [y, m, dd] = value.split('-').map(Number);
    return mesVista.getFullYear() === y && mesVista.getMonth() === m - 1 && d === dd;
  };
  const seleccionarDia = (d) => {
    if (!d) return;
    const yyyy = mesVista.getFullYear();
    const mm = String(mesVista.getMonth() + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    onChange(`${yyyy}-${mm}-${dd}`);
    setAbierto(false);
  };
  const cambiarMes = (delta) => setMesVista((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const nombreMes = mesVista.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={triggerRef}
        onClick={() => setAbierto((a) => !a)}
        sx={{ height: 34, px: 1.2, display: 'flex', alignItems: 'center', gap: 0.8,
          border: `1px solid ${abierto ? IOS.blue : IOS.separator}`, borderRadius: '8px',
          bgcolor: IOS.surface, cursor: 'pointer', minWidth: 110,
          boxShadow: abierto ? `0 0 0 3px ${IOS.blue}18` : 'none', transition: 'all 0.15s' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        <Typography sx={{ fontSize: '0.8rem', color: IOS.label, fontWeight: 600 }}>{fmt(value)}</Typography>
      </Box>

      {abierto && (
        <Box
          ref={popupRef}
          sx={{ position: 'fixed', top: (triggerRef.current?.getBoundingClientRect().bottom || 0) + 6,
            left: triggerRef.current?.getBoundingClientRect().left || 0, zIndex: 99999,
            bgcolor: IOS.surface, borderRadius: '16px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            border: `1px solid ${IOS.separator}`, p: 1.5, width: 250 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box component="button" onClick={() => cambiarMes(-1)} sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', fontSize: '1rem', color: IOS.label2 }}>‹</Box>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'capitalize' }}>{nombreMes}</Typography>
            <Box component="button" onClick={() => cambiarMes(1)} sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', fontSize: '1rem', color: IOS.label2 }}>›</Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 0.3, mb: 0.5 }}>
            {diasSemana.map((d) => (
              <Typography key={d} align="center" sx={{ fontSize: '0.6rem', color: IOS.label2, fontWeight: 700 }}>{d}</Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 0.3 }}>
            {celdas.map((d, i) => (
              <Box key={i} onClick={() => seleccionarDia(d)}
                sx={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px',
                  cursor: d ? 'pointer' : 'default', fontSize: '0.72rem', fontWeight: esSeleccionado(d) ? 700 : 500,
                  color: esSeleccionado(d) ? '#fff' : IOS.label, bgcolor: esSeleccionado(d) ? IOS.blue : 'transparent',
                  border: esHoy(d) && !esSeleccionado(d) ? `1px solid ${IOS.blue}` : '1px solid transparent',
                  '&:hover': { bgcolor: d && !esSeleccionado(d) ? `${IOS.blue}15` : undefined } }}>
                {d || ''}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
const CuentasPorCobrar = ({ modulo = 'economia' }) => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const nc = user?.nombreCompleto || user?.usuario || '';
  const ueId = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const hoy = new Date();
  const haceUnMes = new Date(); haceUnMes.setDate(hoy.getDate() - 30);
  const toISO = (d) => d.toISOString().substring(0, 10);

  const [tab, setTab] = useState('dashboard'); // dashboard | mantenimiento | reportes
  const [desde, setDesde] = useState(toISO(haceUnMes));
  const [hasta, setHasta] = useState(toISO(hoy));
  const [origen, setOrigen] = useState('TODOS');
  const [busqueda, setBusqueda] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('TODOS');
  const [pagina, setPagina] = useState(1);
  const [rpp, setRpp] = useState(50);

  const [kpis, setKpis] = useState(null);
  const [resumen, setResumen] = useState([]);
  const [resumenMensual, setResumenMensual] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [modalVenta, setModalVenta] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [cargandoCuotas, setCargandoCuotas] = useState(false);
  const [editandoCuota, setEditandoCuota] = useState(null);
  const [observacion, setObservacion] = useState('');
  const [exportando, setExportando] = useState(false);

  // Detalle de facturas para una fila de Reportes (aseguradora + mes)
  const [modalDetalle, setModalDetalle] = useState(null);
  const [detalleFacturas, setDetalleFacturas] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [facturaExpandida, setFacturaExpandida] = useState(null);   // `${origen}-${idVenta}`
  const [cuotasCache, setCuotasCache] = useState({});               // { key: [cuotas] }
  const [cargandoCuotasDet, setCargandoCuotasDet] = useState(null); // key en carga

  const toggleCuotasDetalle = async (f) => {
    const key = `${f.origen}-${f.idVenta}`;
    if (facturaExpandida === key) { setFacturaExpandida(null); return; }
    setFacturaExpandida(key);
    if (cuotasCache[key]) return; // ya en caché
    setCargandoCuotasDet(key);
    try {
      const r = await api.get('/aseguradoras/cuotas', { params: { idVenta: f.idVenta, origen: f.origen } });
      if (r.data.success) setCuotasCache(prev => ({ ...prev, [key]: r.data.data || [] }));
    } catch (e) { console.error(e); } finally { setCargandoCuotasDet(null); }
  };

  const CuotasInline = ({ facturaKey }) => {
    const lista = cuotasCache[facturaKey];
    if (cargandoCuotasDet === facturaKey)
      return <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={16} sx={{ color: IOS.purple }} /></Box>;
    if (!lista) return null;
    if (lista.length === 0)
      return <Typography sx={{ py: 1.5, textAlign: 'center', fontSize: '0.72rem', color: IOS.label2 }}>Esta factura no tiene cronograma de cuotas</Typography>;
    return (
      <Box sx={{ py: 1 }}>
        {lista.map((c, i) => {
          const pagada = c.estado === 'PAGADA';
          return (
            <Box key={c.idPago} sx={{ display: 'flex', gap: 1.2, position: 'relative' }}>
              {/* Línea vertical del timeline */}
              {i < lista.length - 1 && (
                <Box sx={{ position: 'absolute', left: 10, top: 22, bottom: -4, width: 2, bgcolor: pagada ? `${IOS.green}40` : IOS.separator }} />
              )}
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, zIndex: 1,
                bgcolor: pagada ? IOS.green : IOS.surface, border: `2px solid ${pagada ? IOS.green : IOS.separator}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {pagada
                  ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5" /></svg>
                  : <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: IOS.label2 }}>{c.cuota}</Typography>}
              </Box>
              <Box sx={{ flex: 1, pb: 1.2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: IOS.label }}>
                    Cuota {c.cuota} · {fmtFecha(c.fechaCuota)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: pagada ? IOS.green : IOS.label }}>{fmtS(c.total)}</Typography>
                    <PillEstado val={c.estado} />
                  </Box>
                </Box>
                {pagada && c.fechaPago && (
                  <Typography sx={{ fontSize: '0.62rem', color: IOS.green }}>Pagada el {fmtFecha(c.fechaPago)}</Typography>
                )}
                {c.observacion && (
                  <Typography sx={{ fontSize: '0.62rem', color: IOS.label2, fontStyle: 'italic' }}>Obs: {c.observacion}</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const origenParam = origen === 'TODOS' ? null : origen;

  const cargarTodo = useCallback(async () => {
    setCargando(true);
    try {
      const [rKpi, rResumen, rResumenMensual, rAlertas, rFacturas] = await Promise.all([
        api.get('/aseguradoras/dashboard-kpis', { params: { ueId, desde, hasta } }),
        api.get('/aseguradoras/resumen', { params: { ueId, desde, hasta, origen: origenParam } }),
        api.get('/aseguradoras/resumen-mensual', { params: { ueId, desde, hasta, origen: origenParam } }),
        api.get('/aseguradoras/cuotas-alerta', { params: { ueId, diasAlerta: 30 } }),
        api.get('/aseguradoras/facturas', { params: { ueId, desde, hasta, origen: origenParam, estadoCobranza: estadoFiltro === 'TODOS' ? null : estadoFiltro } }),
      ]);
      if (rKpi.data.success) setKpis(rKpi.data.data);
      if (rResumen.data.success) setResumen(rResumen.data.data || []);
      if (rResumenMensual.data.success) setResumenMensual(rResumenMensual.data.data || []);
      if (rAlertas.data.success) setAlertas(rAlertas.data.data || []);
      if (rFacturas.data.success) setFacturas(rFacturas.data.data || []);
    } catch (e) { console.error(e); } finally { setCargando(false); }
  }, [ueId, desde, hasta, origenParam, estadoFiltro]);

  useEffect(() => { setPagina(1); cargarTodo(); }, [cargarTodo]);

  const facturasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return facturas;
    return facturas.filter(f =>
      (f.aseguradora || '').toLowerCase().includes(q) ||
      (f.ruc || '').toLowerCase().includes(q) ||
      `${f.serie}-${f.numero}`.toLowerCase().includes(q)
    );
  }, [facturas, busqueda]);

  const facturasPagina = facturasFiltradas.slice((pagina - 1) * rpp, pagina * rpp);

  const abrirCuotas = async (factura) => {
    setModalVenta(factura);
    setCargandoCuotas(true);
    try {
      const r = await api.get('/aseguradoras/cuotas', { params: { idVenta: factura.idVenta, origen: factura.origen } });
      if (r.data.success) setCuotas(r.data.data || []);
    } catch (e) { console.error(e); } finally { setCargandoCuotas(false); }
  };

  const cerrarModal = () => { setModalVenta(null); setCuotas([]); setEditandoCuota(null); setObservacion(''); };

  const marcarPagada = async (cuota) => {
    try {
      await api.post('/aseguradoras/cuotas/actualizar-estado', {
        idPago: cuota.idPago, estado: 'PAGADA', observacion: observacion || cuota.observacion || null,
      });
      await abrirCuotas(modalVenta);
      await cargarTodo();
      setEditandoCuota(null); setObservacion('');
    } catch (e) { console.error(e); }
  };

  const exportarExcel = async () => {
    setExportando(true);
    try {
      const r = await api.get('/aseguradoras/reportes/exportar', { params: { ueId, desde, hasta, origen: origenParam }, responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      a.download = `CuentasPorCobrar_${desde}_${hasta}.xlsx`; a.click();
    } catch (e) { console.error(e); } finally { setExportando(false); }
  };

  const totalesReporte = useMemo(() => facturasFiltradas.reduce((acc, f) => {
    acc.total += Number(f.total || 0);
    if (f.estadoCobranza === 'PAGADA') acc.pagado += Number(f.total || 0);
    else if (f.estadoCobranza === 'PARCIAL') acc.parcial += Number(f.total || 0);
    else acc.pendiente += Number(f.total || 0);
    return acc;
  }, { total: 0, pagado: 0, parcial: 0, pendiente: 0 }), [facturasFiltradas]);

  const nombreMes = (yyyyMM) => {
    if (!yyyyMM) return '';
    const [y, m] = yyyyMM.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
  };

  const gruposPorMes = useMemo(() => {
    const mapa = {};
    resumenMensual.forEach((r) => {
      if (!mapa[r.mes]) mapa[r.mes] = { mes: r.mes, filas: [], totales: { facturado: 0, pagadas: 0, porPagar: 0, anuladas: 0 } };
      mapa[r.mes].filas.push(r);
      mapa[r.mes].totales.facturado += Number(r.totalFacturado || 0);
      mapa[r.mes].totales.pagadas += Number(r.pagadas || 0);
      mapa[r.mes].totales.porPagar += Number(r.porPagar || 0);
      mapa[r.mes].totales.anuladas += Number(r.anuladas || 0);
    });
    return Object.values(mapa).sort((a, b) => b.mes.localeCompare(a.mes));
  }, [resumenMensual]);

  const abrirDetalleFila = async (fila) => {
    setModalDetalle(fila);
    setCargandoDetalle(true);
    try {
      const [y, m] = fila.mes.split('-').map(Number);
      const primerDia = `${fila.mes}-01`;
      const ultimoDia = new Date(y, m, 0).getDate();
      const finMes = `${fila.mes}-${String(ultimoDia).padStart(2, '0')}`;
      const r = await api.get('/aseguradoras/facturas', {
        params: { ueId, desde: primerDia, hasta: finMes, ruc: fila.ruc, origen: origenParam },
      });
      if (r.data.success) setDetalleFacturas(r.data.data || []);
    } catch (e) { console.error(e); } finally { setCargandoDetalle(false); }
  };

  const cerrarDetalleFila = () => { setModalDetalle(null); setDetalleFacturas([]); setFacturaExpandida(null); setCuotasCache({}); setCargandoCuotasDet(null); };

  // ── Panel Comprobante (reutiliza endpoints de ComprobantesPago) ──
  const tipoCpeDe = s => { const c = (s || '').charAt(0).toUpperCase(); return c === 'B' ? 'BOLETA' : c === 'T' ? 'TICKET' : 'FACTURA'; };
  const fuenteDe = o => (o === 'FARMACIA' ? 'DATAFARMA' : 'CAJA');

  const [cpePanel, setCpePanel] = useState(null);          // factura seleccionada
  const [cpeDetalle, setCpeDetalle] = useState([]);
  const [cpeCargando, setCpeCargando] = useState(false);
  const [cpeCdrDisp, setCpeCdrDisp] = useState(false);
  const [cpeXmlDisp, setCpeXmlDisp] = useState(false);
  const [cpeDescargando, setCpeDescargando] = useState(null); // 'xml' | 'cdr'
  const [cpeModalFormato, setCpeModalFormato] = useState(false);
  const [cpeImprimiendo, setCpeImprimiendo] = useState(false);

  useEffect(() => {
    document.body.style.overflow = cpePanel ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [cpePanel]);

  const abrirCpe = async (f) => {
    setCpePanel(f); setCpeCargando(true); setCpeDetalle([]); setCpeCdrDisp(false); setCpeXmlDisp(false);
    try {
      const r = await api.get('/economia/ComprobantesPago/detalle-venta', {
        params: { idVenta: f.idVenta, tipoCPE: tipoCpeDe(f.serie), serie: f.serie, numero: f.numero, ueId }
      });
      if (r.data.success) {
        setCpeDetalle(r.data.data || []);
        setCpeCdrDisp(!!r.data.cdrDisponible);
        setCpeXmlDisp(!!r.data.xmlOriginalDisponible);
      }
    } catch (e) { console.error(e); } finally { setCpeCargando(false); }
  };

  const cpeDescargar = async (tipo) => {
    if (!cpePanel) return;
    setCpeDescargando(tipo);
    try {
      const endpoint = tipo === 'xml' ? 'descargar-xml-original' : 'descargar-cdr';
      const params = { tipoCPE: tipoCpeDe(cpePanel.serie), serie: cpePanel.serie, numero: cpePanel.numero, ueId };
      if (tipo === 'xml') { params.idVenta = cpePanel.idVenta; params.modulo = fuenteDe(cpePanel.origen); }
      const r = await api.get(`/economia/ComprobantesPago/${endpoint}`, { params, responseType: 'blob' });
      const nombre = tipo === 'xml' ? `${cpePanel.serie}-${cpePanel.numero}.xml` : `R-${cpePanel.serie}-${cpePanel.numero}.xml`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      a.download = nombre; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'No se pudo descargar el documento.');
    } finally { setCpeDescargando(null); }
  };

  const cpeImprimir = async (fmt) => {
    if (!cpePanel) return; setCpeImprimiendo(true);
    try {
      const r = await api.get('/economia/ComprobantesPago/imprimir', {
        params: { idVenta: cpePanel.idVenta, fuente: fuenteDe(cpePanel.origen), tipoCPE: tipoCpeDe(cpePanel.serie), formato: fmt, ueId },
        responseType: 'blob'
      });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      setCpeModalFormato(false);
    } catch (e) { console.error(e); } finally { setCpeImprimiendo(false); }
  };

  const kpiCards = [
    { label: 'Total Facturado', monto: kpis?.totalFacturado, color: IOS.blue,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.8" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg> },
    { label: 'Pagado', monto: kpis?.totalPagado, color: IOS.green,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="1.8" strokeLinecap="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg> },
    { label: 'Parcial', monto: kpis?.totalParcial, color: IOS.orange,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.orange} strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg> },
    { label: 'Por Cobrar', monto: kpis?.totalPorCobrar, color: IOS.red,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="1.8" strokeLinecap="round"><path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg> },
  ];

  const TabBtn = ({ id, label }) => (
    <Box onClick={() => setTab(id)}
      sx={{ px: { xs: 1, sm: 2.5 }, py: 0.8, borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s',
        flex: { xs: 1, sm: '0 0 auto' }, textAlign: 'center',
        bgcolor: tab === id ? IOS.bg : 'transparent', boxShadow: tab === id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
      <Typography sx={{ fontSize: { xs: '0.76rem', sm: '0.82rem' }, fontWeight: tab === id ? 700 : 500, color: tab === id ? IOS.label : IOS.label2, whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: IOS.bg }}>
        <HeaderIOS titulo="Cuentas por Cobrar" subtitulo="Economía"
          onHome={() => navigate(`/${modulo}`)} onLogout={handleLogout}
          nombreCompleto={nc} avatarLetra={nc.charAt(0).toUpperCase()} />

        <Box sx={{ maxWidth: 1600, mx: 'auto', px: { xs: 1.5, sm: 2.6 }, pt: 2.5, pb: 6 }}>

          {/* Tabs principales — separado de los filtros, como en Requerimiento */}
          <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',mb:2.5,flexWrap:'wrap',gap:1.5}}>
            <Box sx={{display:'flex',gap:0.5,bgcolor:IOS.surface,borderRadius:'12px',
              p:0.5,boxShadow:'0 1px 4px rgba(0,0,0,0.07)',width:{xs:'100%',sm:'auto'}}}>
              <TabBtn id="dashboard" label="Dashboard" />
              <TabBtn id="mantenimiento" label="Mantenimiento" />
              <TabBtn id="reportes" label="Reportes" />
            </Box>
          </Box>

          {/* Filtros */}
          <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2, mb: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
            <Box sx={{ display: { xs: 'grid', sm: 'flex' }, gridTemplateColumns: '1fr 1fr', gap: 1.5, flexWrap: 'wrap', alignItems: { sm: 'flex-end' } }}>
              <Box>
                <Lbl>Desde</Lbl>
                <DatePickerIOS value={desde} onChange={setDesde} placeholder="Desde" />
              </Box>
              <Box>
                <Lbl>Hasta</Lbl>
                <DatePickerIOS value={hasta} onChange={setHasta} placeholder="Hasta" />
              </Box>
              <Box sx={{ gridColumn: { xs: '1 / -1' } }}>
                <Lbl>Origen</Lbl>
                <Sel value={origen} onChange={e => setOrigen(e.target.value)} w={{ xs: '100%', sm: 170 }}>
                  <option value="TODOS">Todos (Caja + Farmacia)</option>
                  <option value="FARMACIA">Solo Farmacia</option>
                  <option value="CAJA">Solo Caja</option>
                </Sel>
              </Box>
            </Box>
          </Box>

          {cargando ? (
            <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: IOS.blue }} /></Box>
          ) : (
            <>
              {/* ═══════════ DASHBOARD ═══════════ */}
              {tab === 'dashboard' && (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4,1fr)' }, gap: 1.5, mb: 2 }}>
                    {kpiCards.map(k => <KpiCard key={k.label} {...k} />)}
                  </Box>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2 }}>
                    {/* Resumen por aseguradora — agrupado por mes */}
                    <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}`, maxHeight: 520, overflowY: 'auto' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: IOS.label, mb: 2 }}>Por Aseguradora / Cliente</Typography>
                      {gruposPorMes.length === 0 && (
                        <Typography sx={{ color: IOS.label2, fontSize: '0.8rem', textAlign: 'center', py: 4 }}>Sin facturas al crédito en el período seleccionado.</Typography>
                      )}
                      {gruposPorMes.map(grupo => (
                        <Box key={grupo.mes} sx={{ mb: 3 }}>
                          <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, pb: 0.5, borderBottom: `1px solid ${IOS.separator}` }}>
                            {nombreMes(grupo.mes)}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {grupo.filas.map(r => {
                            const seg = v => Math.max(0, Number(v) || 0);
                            const pag = seg(r.pagadas), por = seg(r.porPagar), anu = seg(r.anuladas);
                            return (
                              <Box key={r.ruc + r.aseguradora}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: IOS.label }}>{r.aseguradora}</Typography>
                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: IOS.label }}>{fmtS(r.totalFacturado)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', height: 8, borderRadius: '5px', overflow: 'hidden', bgcolor: IOS.bg }}>
                                  {pag > 0 && <Box sx={{ flex: pag, minWidth: 3, bgcolor: IOS.green }} />}
                                  {por > 0 && <Box sx={{ flex: por, minWidth: 3, bgcolor: IOS.orange }} />}
                                  {anu > 0 && <Box sx={{ flex: anu, minWidth: 3, bgcolor: IOS.red }} />}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5, rowGap: 0.2, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: IOS.green }}>● Pagado {fmtS(r.pagadas)}</Typography>
                                  <Typography sx={{ fontSize: '0.62rem', color: IOS.orange }}>● Por cobrar {fmtS(r.porPagar)}</Typography>
                                  <Typography sx={{ fontSize: '0.62rem', color: IOS.red }}>● Anulado {fmtS(r.anuladas)}</Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>

                    {/* Alertas */}
                    <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', p: 2.5, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}` }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: IOS.label, mb: 2 }}>Cuotas próximas a vencer</Typography>
                      {alertas.length === 0 && (
                        <Typography sx={{ color: IOS.label2, fontSize: '0.8rem', textAlign: 'center', py: 4 }}>No hay cuotas venciendo en los próximos 7 días 🎉</Typography>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, maxHeight: 380, overflowY: 'auto' }}>
                        {alertas.map(a => (
                          <Box key={a.idPago} onClick={() => a.idVenta && abrirCpe({ idVenta: a.idVenta, origen: a.origen, serie: a.serie, numero: a.numero, aseguradora: a.aseguradora, total: a.total, fecha: a.fechaCuota, estadoCobranza: 'PENDIENTE' })}
                            sx={{ p: 1.4, borderRadius: '12px', cursor: a.idVenta ? 'pointer' : 'default', bgcolor: a.diasParaVencer < 0 ? '#FFF0EF' : '#FFF8E6', border: `1px solid ${a.diasParaVencer < 0 ? '#FFD4D1' : '#FFE9A0'}`, '&:hover': { filter: a.idVenta ? 'brightness(0.98)' : 'none' } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.label }}>{a.aseguradora}</Typography>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.label }}>{fmtS(a.total)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.66rem', color: IOS.label2 }}>{a.serie}-{a.numero} · Cuota {a.cuota} · {fmtFecha(a.fechaCuota)}</Typography>
                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: a.diasParaVencer < 0 ? IOS.red : IOS.orange }}>
                              {a.diasParaVencer < 0 ? `Vencida hace ${Math.abs(a.diasParaVencer)} día(s)` : `Vence en ${a.diasParaVencer} día(s)`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {/* ═══════════ MANTENIMIENTO ═══════════ */}
              {tab === 'mantenimiento' && (
                <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${IOS.separator}`, overflow: 'hidden' }}>
                  <Box sx={{ p: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: `1px solid ${IOS.separator}` }}>
                    <Box sx={{ flex: '1 1 220px', maxWidth: 280 }}>
                      <Lbl>Buscar</Lbl>
                      <Box sx={{ position: 'relative' }}>
                        <Box sx={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: IOS.label2 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                        </Box>
                        <input type="text" placeholder="Aseguradora, RUC, comprobante..." value={busqueda}
                          onChange={e => { setBusqueda(e.target.value); setPagina(1); }}
                          style={{ width: '100%', height: 34, paddingLeft: 28, paddingRight: 10, border: `1px solid ${IOS.separator}`, borderRadius: 8, fontSize: '0.8rem', color: IOS.label, backgroundColor: IOS.surface, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Lbl>Estado</Lbl>
                      <Sel value={estadoFiltro} onChange={e => { setEstadoFiltro(e.target.value); setPagina(1); }} w={140}>
                        <option value="TODOS">TODOS</option>
                        <option value="PENDIENTE">PENDIENTE</option>
                        <option value="PARCIAL">PARCIAL</option>
                        <option value="PAGADA">PAGADA</option>
                      </Sel>
                    </Box>
                  </Box>

                  <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto', overflowY: 'auto', maxHeight: 520 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900, fontSize: '0.75rem' }}>
                      <thead><tr>
                        <TH w={90}>Origen</TH><TH>Aseguradora / Cliente</TH><TH w={100}>RUC</TH>
                        <TH w={100}>Comprobante</TH><TH w={90}>Fecha</TH><TH right w={100}>Total</TH>
                        <TH center w={100}>Estado</TH><TH center w={60}></TH>
                      </tr></thead>
                      <tbody>
                        {facturasPagina.length === 0
                          ? <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: IOS.label2, fontSize: '0.82rem' }}>Sin facturas al crédito</td></tr>
                          : facturasPagina.map((f) => (
                            <tr key={`${f.origen}-${f.idVenta}`} style={{ borderBottom: `1px solid ${IOS.separator}` }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = IOS.bg}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = IOS.surface}>
                              <td style={{ padding: '8px' }}><PillOrigen val={f.origen} /></td>
                              <td style={{ padding: '8px', color: IOS.label, fontSize: '0.74rem' }}>{f.aseguradora}</td>
                              <td style={{ padding: '8px', fontFamily: 'monospace', color: IOS.label2, fontSize: '0.68rem' }}>{f.ruc}</td>
                              <td style={{ padding: '8px', fontFamily: 'monospace', color: IOS.label2, fontSize: '0.68rem' }}>{f.serie}-{f.numero}</td>
                              <td style={{ padding: '8px', color: IOS.label2, fontSize: '0.7rem' }}>{fmtFecha(f.fecha)}</td>
                              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 700, color: IOS.label, fontSize: '0.74rem' }}>{fmtS(f.total)}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}><PillEstado val={f.estadoCobranza} /></td>
                              <td style={{ padding: '4px', textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Ver cronograma" placement="top">
                                    <Box onClick={() => abrirCuotas(f)}
                                      sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: `${IOS.blue}12`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: `${IOS.blue}22` } }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                    </Box>
                                  </Tooltip>
                                  <Tooltip title="Ver comprobante / Reimprimir" placement="top">
                                    <Box onClick={() => abrirCpe(f)}
                                      sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: `${IOS.green}12`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: `${IOS.green}22` } }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </Box>
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {facturasPagina.length === 0
                      ? <Box sx={{ py: 6, textAlign: 'center' }}><Typography sx={{ color: IOS.label2, fontSize: '0.82rem' }}>Sin facturas al crédito</Typography></Box>
                      : facturasPagina.map(f => (
                        <Box key={`${f.origen}-${f.idVenta}`} onClick={() => abrirCuotas(f)}
                          sx={{ p: 2, borderBottom: `1px solid ${IOS.separator}`, cursor: 'pointer', '&:active': { bgcolor: IOS.bg } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.8, gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: IOS.label, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.aseguradora}</Typography>
                              <Typography sx={{ fontSize: '0.66rem', color: IOS.label2, fontFamily: 'monospace' }}>{f.serie}-{f.numero} · {fmtFecha(f.fecha)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color: IOS.label, flexShrink: 0 }}>{fmtS(f.total)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 0.8 }}>
                              <PillOrigen val={f.origen} />
                              <PillEstado val={f.estadoCobranza} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
                              <Typography onClick={e => { e.stopPropagation(); abrirCpe(f); }}
                                sx={{ fontSize: '0.66rem', fontWeight: 700, color: IOS.green }}>Comprobante ›</Typography>
                              <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: IOS.blue }}>Cronograma ›</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                  </Box>
                  <Paginacion total={facturasFiltradas.length} pagina={pagina} setPagina={setPagina} rpp={rpp} setRpp={setRpp} />
                </Box>
              )}

              {/* ═══════════ REPORTES ═══════════ */}
{tab === 'reportes' && (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: IOS.label }}>
        Resumen por mes y aseguradora
      </Typography>
      <Tooltip title="Exportar Excel (detalle completo)" placement="top">
        <Box component="button" onClick={() => !exportando && facturasFiltradas.length > 0 && exportarExcel()}
          sx={{ width: 34, height: 34, borderRadius: '50%', border: 'none', bgcolor: IOS.green,
            cursor: facturasFiltradas.length === 0 ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: facturasFiltradas.length === 0 ? 0.4 : 1, transition: 'all 0.15s', '&:hover': { filter: 'brightness(0.9)' } }}>
          {exportando ? <CircularProgress size={14} sx={{ color: 'white' }} /> : (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round"><path d="M12 3v13M7 11l5 5 5-5" /><path d="M4 20h16" /></svg>
          )}
        </Box>
      </Tooltip>
    </Box>

    {gruposPorMes.length === 0 && (
      <Box sx={{ bgcolor: IOS.surface, borderRadius: '16px', border: `1px solid ${IOS.separator}`, py: 6, textAlign: 'center' }}>
        <Typography sx={{ color: IOS.label2, fontSize: '0.82rem' }}>Sin facturas al crédito en el período seleccionado.</Typography>
      </Box>
    )}

    {gruposPorMes.map((grupo) => (
      <Box key={grupo.mes} sx={{ bgcolor: IOS.surface, borderRadius: '16px', border: `1px solid ${IOS.separator}`, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
        <Box sx={{ bgcolor: '#FFE9A0', px: 2.5, py: 1.2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: '#5C4A00', textTransform: 'uppercase', textAlign: 'center' }}>
            RESUMEN {nombreMes(grupo.mes)}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4,1fr)' }, borderBottom: `1px solid ${IOS.separator}` }}>
          {[
            ['TOTAL FACTURADAS', grupo.totales.facturado, IOS.label],
            ['PAGADAS', grupo.totales.pagadas, IOS.green],
            ['POR PAGAR', grupo.totales.porPagar, IOS.orange],
            ['ANULADAS', grupo.totales.anuladas, IOS.red],
          ].map(([lbl, val, color], i) => (
            <Box key={lbl} sx={{ px: 1, py: 1.4, textAlign: 'center',
              borderLeft: { xs: i % 2 === 1 ? `1px solid ${IOS.separator}` : 'none', sm: i > 0 ? `1px solid ${IOS.separator}` : 'none' },
              borderTop: { xs: i > 1 ? `1px solid ${IOS.separator}` : 'none', sm: 'none' }, bgcolor: '#FFF6DF' }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#8B6914', letterSpacing: '0.05em', mb: 0.3, whiteSpace: 'nowrap' }}>{lbl}</Typography>
              <Typography sx={{ fontSize: { xs: '0.84rem', sm: '0.92rem' }, fontWeight: 800, color, whiteSpace: 'nowrap' }}>{fmtS(val)}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ bgcolor: '#FFF9C4', px: 2.5, py: 0.8, borderBottom: `1px solid ${IOS.separator}` }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#5C4A00', textTransform: 'uppercase', textAlign: 'center' }}>
            RESUMEN POR ASEGURADORA {nombreMes(grupo.mes)}
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760, fontSize: '0.75rem' }}>
            <thead><tr>
              <TH>Aseguradora</TH>
              <TH right w={130}>Total Facturado</TH>
              <TH right w={120}>Pagadas</TH>
              <TH right w={120}>Por Pagar</TH>
              <TH right w={120}>Anuladas</TH>
              <TH center w={60}></TH>
            </tr></thead>
            <tbody>
              {grupo.filas.map((f) => (
                <tr key={f.ruc + f.aseguradora} style={{ borderBottom: `1px solid ${IOS.separator}` }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = IOS.bg}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = IOS.surface}>
                  <td style={{ padding: '9px 8px', color: IOS.label, fontWeight: 600 }}>{f.aseguradora}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: IOS.label, fontWeight: 700 }}>{fmtS(f.totalFacturado)}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: IOS.green, fontWeight: 700 }}>{fmtS(f.pagadas)}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: IOS.orange, fontWeight: 700 }}>{fmtS(f.porPagar)}</td>
                  <td style={{ padding: '9px 8px', textAlign: 'right', color: IOS.red, fontWeight: 700 }}>{fmtS(f.anuladas)}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <Tooltip title="Ver detalle de facturas" placement="top">
                      <Box onClick={() => abrirDetalleFila(f)}
                        sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: `${IOS.purple}12`,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          '&:hover': { bgcolor: `${IOS.purple}22` } }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </Box>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
          {grupo.filas.map(f => (
            <Box key={f.ruc + f.aseguradora} sx={{ p: 1.5, mb: 1, bgcolor: IOS.bg, borderRadius: '12px', border: `1px solid ${IOS.separator}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: IOS.label, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.aseguradora}</Typography>
                <Box component="button" onClick={() => abrirDetalleFila(f)}
                  sx={{ height: 26, px: 1.2, borderRadius: '8px', border: `1px solid ${IOS.purple}30`, bgcolor: `${IOS.purple}08`,
                    cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: `${IOS.purple}14` } }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: IOS.purple }}>Detalle</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8 }}>
                {[
                  ['Facturado', f.totalFacturado, IOS.label],
                  ['Pagadas', f.pagadas, IOS.green],
                  ['Por pagar', f.porPagar, IOS.orange],
                  ['Anuladas', f.anuladas, IOS.red],
                ].map(([lbl, val, color]) => (
                  <Box key={lbl} sx={{ bgcolor: IOS.surface, borderRadius: '8px', p: 1 }}>
                    <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.2 }}>{lbl}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color }}>{fmtS(val)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
)}
            </>
          )}
        </Box>
      </Box>

      {/* Panel Comprobante (reutiliza endpoints de ComprobantesPago) */}
      {cpePanel && (
        <>
          <Box onClick={() => setCpePanel(null)}
            sx={{ position: 'fixed', inset: 0, zIndex: 10500, bgcolor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(2px)' }} />
          <Box sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: { xs: '100%', sm: 480 }, zIndex: 10600,
            bgcolor: IOS.surface, boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: IOS.separator, borderRadius: 4 } }}>
            <Box sx={{ px: 2.5, pt: 2.5, pb: 2, borderBottom: `1px solid ${IOS.separator}`, flexShrink: 0,
              position: 'sticky', top: 0, zIndex: 2, bgcolor: IOS.surface,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: IOS.label2, letterSpacing: '0.07em', textTransform: 'uppercase', mb: 0.3 }}>Detalle</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: IOS.label }}>Comprobante de Pago</Typography>
              </Box>
              <Box component="button" onClick={() => setCpePanel(null)}
                sx={{ width: 30, height: 30, borderRadius: '50%', border: 'none', bgcolor: '#E5E5EA', cursor: 'pointer', flexShrink: 0, p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: '#D1D1D6' } }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </Box>
            </Box>
            <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${IOS.separator}`, bgcolor: IOS.bg }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: IOS.label, mb: 1.5 }}>{cpePanel.aseguradora}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                {[['Comprobante', `${cpePanel.serie}-${cpePanel.numero}`], ['Fecha', fmtFecha(cpePanel.fecha)], ['Origen', cpePanel.origen], ['Cobranza', cpePanel.estadoCobranza || '—']].map(([lbl, val]) => (
                  <Box key={lbl} sx={{ p: 1.2, bgcolor: IOS.surface, borderRadius: '10px', border: `1px solid ${IOS.separator}` }}>
                    <Typography sx={{ fontSize: '0.58rem', color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{lbl}</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: IOS.label, mt: 0.2 }}>{val}</Typography>
                  </Box>
                ))}
                <Box sx={{ p: 1.2, bgcolor: `${IOS.blue}08`, borderRadius: '10px', border: `1px solid ${IOS.blue}25`, gridColumn: '1 / -1' }}>
                  <Typography sx={{ fontSize: '0.58rem', color: IOS.blue, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Total</Typography>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: IOS.blue, mt: 0.2 }}>{fmtS(cpePanel.total)}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Box component="button" onClick={() => setCpeModalFormato(true)}
                  sx={{ flex: 1, height: 34, borderRadius: '10px', border: `1px solid ${IOS.blue}30`, bgcolor: `${IOS.blue}08`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, '&:hover': { bgcolor: `${IOS.blue}14` } }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: IOS.blue }}>Reimprimir</Typography>
                </Box>
              </Box>
              {!cpeCargando && cpeDetalle.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box component="button" onClick={() => cpeDescargar('xml')} disabled={cpeDescargando === 'xml'}
                    sx={{ flex: 1, height: 32, borderRadius: '10px', border: '1px solid #6ee7b7', bgcolor: '#ecfdf5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, '&:hover': { bgcolor: '#d1fae5' } }}>
                    {cpeDescargando === 'xml'
                      ? <CircularProgress size={11} sx={{ color: '#047857' }} />
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#047857' }}>{cpeXmlDisp ? 'XML original' : 'XML (reconstruir)'}</Typography>
                  </Box>
                  <Box component="button" onClick={() => cpeCdrDisp && cpeDescargar('cdr')} disabled={!cpeCdrDisp || cpeDescargando === 'cdr'}
                    sx={{ flex: 1, height: 32, borderRadius: '10px', border: `1px solid ${cpeCdrDisp ? '#93c5fd' : IOS.separator}`, bgcolor: cpeCdrDisp ? '#eff6ff' : IOS.bg, cursor: cpeCdrDisp ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, opacity: cpeCdrDisp ? 1 : 0.5, '&:hover': { bgcolor: cpeCdrDisp ? '#dbeafe' : IOS.bg } }}>
                    {cpeDescargando === 'cdr'
                      ? <CircularProgress size={11} sx={{ color: '#1d4ed8' }} />
                      : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={cpeCdrDisp ? '#1d4ed8' : IOS.label2} strokeWidth="2" strokeLinecap="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>}
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: cpeCdrDisp ? '#1d4ed8' : IOS.label2 }}>CDR SUNAT</Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: IOS.label }}>Detalle de Productos</Typography>
              </Box>
              {cpeCargando
                ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: IOS.blue }} /></Box>
                : cpeDetalle.length === 0
                  ? <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: IOS.label2, fontSize: '0.8rem' }}>Sin detalle disponible</Typography></Box>
                  : <Box sx={{ px: 2, pb: 2 }}>
                      {cpeDetalle.map((d, i) => (
                        <Box key={i} sx={{ p: 1.5, mb: 1, bgcolor: IOS.bg, borderRadius: '12px', border: `1px solid ${IOS.separator}` }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.label, lineHeight: 1.3, mb: 0.3 }}>{d.item}</Typography>
                          <Typography sx={{ fontSize: '0.62rem', fontFamily: 'monospace', color: IOS.label2, mb: 1 }}>{d.codigoProcedimiento}</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              {[['Cant.', d.vtdCantidad], ['Precio', Number(d.vtdPrecioUnitario || 0).toFixed(2)], ['Desc.', Number(d.vtdDescuento || 0).toFixed(2)]].map(([lbl, val]) => (
                                <Box key={lbl}>
                                  <Typography sx={{ fontSize: '0.55rem', color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{lbl}</Typography>
                                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: IOS.label }}>{val}</Typography>
                                </Box>
                              ))}
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '0.55rem', color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Subtotal</Typography>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: IOS.label }}>{Number(d.subtotal || 0).toFixed(2)}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      <Box sx={{ p: 1.5, bgcolor: `${IOS.blue}08`, borderRadius: '12px', border: `1px solid ${IOS.blue}25`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total (S/)</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: IOS.blue }}>{cpeDetalle.reduce((s, d) => s + Number(d.subtotal || 0), 0).toFixed(2)}</Typography>
                      </Box>
                    </Box>
              }
            </Box>
          </Box>
        </>
      )}

      {/* Modal formato de impresión del panel CPE */}
      <Modal open={cpeModalFormato && !!cpePanel} onClose={() => !cpeImprimiendo && setCpeModalFormato(false)} w={380}>
        <ModalHeader title="Formato de Impresión" onClose={() => !cpeImprimiendo && setCpeModalFormato(false)} color={IOS.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>} />
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: '0.8rem', color: IOS.label, fontWeight: 600, mb: 0.3 }}>{cpePanel ? `${cpePanel.serie}-${cpePanel.numero}` : ''}</Typography>
          <Typography sx={{ fontSize: '0.74rem', color: IOS.label2, mb: 2 }}>{cpePanel?.aseguradora}</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
            {[{ fmt: 'A4', label: 'Formato A4', sub: 'Hoja completa', color: IOS.blue }, { fmt: 'TICKET', label: 'Ticket', sub: '80mm / POS', color: IOS.orange }].map(({ fmt, label, sub, color }) => (
              <Box key={fmt} component="button" onClick={() => !cpeImprimiendo && cpeImprimir(fmt)} disabled={cpeImprimiendo}
                sx={{ flex: 1, py: 2.5, borderRadius: '14px', border: `2px solid ${color}30`, bgcolor: `${color}08`, cursor: cpeImprimiendo ? 'default' : 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, transition: 'all 0.2s', '&:hover': { bgcolor: `${color}14`, borderColor: `${color}60` } }}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color }}>{label}</Typography>
                <Typography sx={{ fontSize: '0.65rem', color: IOS.label2 }}>{sub}</Typography>
              </Box>
            ))}
          </Box>
          {cpeImprimiendo && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}><CircularProgress size={20} sx={{ color: IOS.blue }} /></Box>}
        </Box>
      </Modal>

      {/* Modal cronograma de cuotas */}
      <Modal open={!!modalVenta} onClose={cerrarModal} w={460}>
        <ModalHeader
          title={`Cronograma — ${modalVenta?.aseguradora || ''}`}
          sub={modalVenta ? `${modalVenta.serie}-${modalVenta.numero} · ${fmtS(modalVenta.total)}` : ''}
          onClose={cerrarModal}
          color={IOS.blue}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
        />
        <Box sx={{ p: 2.5 }}>
          {cargandoCuotas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: IOS.blue }} /></Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {cuotas.map((c) => (
                <Box key={c.idPago} sx={{ p: 1.4, borderRadius: '12px', border: `1px solid ${IOS.separator}`, bgcolor: IOS.bg }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.8 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: IOS.label }}>Cuota {c.cuota} — {fmtFecha(c.fechaCuota)}</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: IOS.label2 }}>{fmtS(c.total)}</Typography>
                      {c.observacion && <Typography sx={{ fontSize: '0.64rem', color: IOS.label2 }}>Obs: {c.observacion}</Typography>}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PillEstado val={c.estado} />
                      {c.estado !== 'PAGADA' && (
                        <Box component="button"
                          onClick={() => editandoCuota === c.idPago ? marcarPagada(c) : setEditandoCuota(c.idPago)}
                          sx={{ height: 28, px: 1.2, borderRadius: '8px', border: 'none', bgcolor: IOS.green, color: 'white',
                            fontWeight: 700, fontSize: '0.68rem', cursor: 'pointer', '&:hover': { filter: 'brightness(0.92)' } }}>
                          {editandoCuota === c.idPago ? 'Confirmar' : 'Marcar pagada'}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {editandoCuota === c.idPago && (
                    <input type="text" placeholder="Observación (opcional)" value={observacion}
                      onChange={e => setObservacion(e.target.value)}
                      style={{ width: '100%', height: 32, marginTop: 8, padding: '0 10px', border: `1px solid ${IOS.separator}`, borderRadius: 8, fontSize: '0.76rem', color: IOS.label, backgroundColor: IOS.surface, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
</Modal>

      {/* Modal detalle de facturas por aseguradora+mes (botón "Ver detalle" de Reportes) */}
      <Modal open={!!modalDetalle} onClose={cerrarDetalleFila} w={620}>
        <ModalHeader
          title={`Detalle — ${modalDetalle?.aseguradora || ''}`}
          sub={modalDetalle ? nombreMes(modalDetalle.mes) : ''}
          onClose={cerrarDetalleFila}
          color={IOS.purple}
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
        />
        <Box sx={{ p: 0 }}>
          {cargandoDetalle ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={22} sx={{ color: IOS.purple }} /></Box>
          ) : detalleFacturas.length === 0 ? (
            <Box sx={{ py: 5, textAlign: 'center' }}>
              <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: `${IOS.purple}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              </Box>
              <Typography sx={{ color: IOS.label2, fontSize: '0.82rem' }}>Sin facturas en este mes</Typography>
            </Box>
          ) : (
            <>
              {/* Strip de resumen */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', borderBottom: `1px solid ${IOS.separator}`, bgcolor: IOS.bg }}>
                {[
                  ['Facturas', detalleFacturas.length, IOS.label],
                  ['Pagadas', detalleFacturas.filter(f => f.estadoCobranza === 'PAGADA').length, IOS.green],
                  ['Pendientes', detalleFacturas.filter(f => f.estadoCobranza !== 'PAGADA').length, IOS.orange],
                ].map(([lbl, val, color], i) => (
                  <Box key={lbl} sx={{ px: 2, py: 1.4, textAlign: 'center', borderLeft: i > 0 ? `1px solid ${IOS.separator}` : 'none' }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.2 }}>{lbl}</Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color, lineHeight: 1 }}>{val}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Desktop — tabla */}
              <Box sx={{ display: { xs: 'none', sm: 'block' }, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                  <thead><tr>
                    <TH w={80}>Origen</TH><TH w={110}>Comprobante</TH><TH w={90}>Fecha</TH>
                    <TH right w={110}>Total</TH><TH center w={100}>Estado</TH>
                  </tr></thead>
                  <tbody>
                    {detalleFacturas.map(f => {
                      const key = `${f.origen}-${f.idVenta}`;
                      const abierta = facturaExpandida === key;
                      return (
                        <React.Fragment key={key}>
                          <tr onClick={() => toggleCuotasDetalle(f)}
                            style={{ borderBottom: abierta ? 'none' : `1px solid ${IOS.separator}`, transition: 'background 0.1s', cursor: 'pointer',
                              backgroundColor: abierta ? `${IOS.purple}06` : 'transparent' }}
                            onMouseEnter={e => { if (!abierta) e.currentTarget.style.backgroundColor = IOS.bg; }}
                            onMouseLeave={e => { if (!abierta) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                            <td style={{ padding: '9px 10px' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <svg width="9" height="9" viewBox="0 0 24 24" style={{ transform: abierta ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
                                  <path d="M9 6l6 6-6 6" stroke={IOS.label2} strokeWidth="2.5" fill="none" strokeLinecap="round" />
                                </svg>
                                <PillOrigen val={f.origen} />
                              </Box>
                            </td>
                            <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: IOS.label, fontWeight: 600 }}>{f.serie}-{f.numero}</td>
                            <td style={{ padding: '9px 10px', color: IOS.label2 }}>{fmtFecha(f.fecha)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700, color: IOS.label }}>{fmtS(f.total)}</td>
                            <td style={{ padding: '9px 10px', textAlign: 'center' }}><PillEstado val={f.estadoCobranza} /></td>
                          </tr>
                          {abierta && (
                            <tr style={{ borderBottom: `1px solid ${IOS.separator}` }}>
                              <td colSpan={5} style={{ padding: '0 14px 8px 30px', backgroundColor: `${IOS.purple}06` }}>
                                <CuotasInline facturaKey={key} />
                                <Box component="button" onClick={e => { e.stopPropagation(); abrirCpe(f); }}
                                  sx={{ mb: 1, height: 28, px: 1.5, borderRadius: '8px', border: `1px solid ${IOS.green}30`, bgcolor: `${IOS.green}08`,
                                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.6, '&:hover': { bgcolor: `${IOS.green}14` } }}>
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: IOS.green }}>Ver comprobante / Reimprimir</Typography>
                                </Box>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: IOS.bg, borderTop: `2px solid ${IOS.separator}` }}>
                      <td colSpan={3} style={{ padding: '10px', fontSize: '0.72rem', fontWeight: 700, color: IOS.label2, textAlign: 'right' }}>TOTAL:</td>
                      <td style={{ padding: '10px', fontSize: '0.86rem', fontWeight: 900, color: IOS.purple, textAlign: 'right' }}>
                        {fmtS(detalleFacturas.reduce((s, f) => s + Number(f.total || 0), 0))}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </Box>

              {/* Móvil — cards */}
              <Box sx={{ display: { xs: 'block', sm: 'none' }, p: 1.5 }}>
                {detalleFacturas.map(f => {
                  const key = `${f.origen}-${f.idVenta}`;
                  const abierta = facturaExpandida === key;
                  return (
                    <Box key={key} onClick={() => toggleCuotasDetalle(f)}
                      sx={{ p: 1.5, mb: 1, bgcolor: IOS.bg, borderRadius: '12px',
                        border: `1px solid ${abierta ? `${IOS.purple}50` : IOS.separator}`, cursor: 'pointer' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: IOS.label, fontFamily: 'monospace' }}>{f.serie}-{f.numero}</Typography>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: IOS.label }}>{fmtS(f.total)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 0.8 }}>
                          <PillOrigen val={f.origen} />
                          <PillEstado val={f.estadoCobranza} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ fontSize: '0.68rem', color: IOS.label2 }}>{fmtFecha(f.fecha)}</Typography>
                          <svg width="9" height="9" viewBox="0 0 24 24" style={{ transform: abierta ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                            <path d="M9 6l6 6-6 6" stroke={IOS.label2} strokeWidth="2.5" fill="none" strokeLinecap="round" />
                          </svg>
                        </Box>
                      </Box>
                      {abierta && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${IOS.separator}` }} onClick={e => e.stopPropagation()}>
                          <CuotasInline facturaKey={key} />
                          <Box component="button" onClick={() => abrirCpe(f)}
                            sx={{ width: '100%', height: 30, borderRadius: '8px', border: `1px solid ${IOS.green}30`, bgcolor: `${IOS.green}08`,
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: IOS.green }}>Ver comprobante / Reimprimir</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <Box sx={{ p: 1.5, bgcolor: `${IOS.purple}08`, borderRadius: '12px', border: `1px solid ${IOS.purple}25`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: IOS.label2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: IOS.purple }}>{fmtS(detalleFacturas.reduce((s, f) => s + Number(f.total || 0), 0))}</Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default CuentasPorCobrar;